/**
 * 将 base64 字符串转换为文件对象
 * @param {string} base64 - base64 编码的字符串
 * @param {string} fileName - 文件名
 * @param {string} [type] - 文件类型（可选）
 * @returns {File} - 转换后的文件对象
 */
export function base64ToFile(base64, fileName, type) {
  let arr = base64.split(",");
  let bstr = atob(arr[1]);
  let n = bstr.length;
  let u8arr = new Uint8Array(n);
  let mime = "";
  if (!type) {
    try {
      mime = arr[0].match(/:(.*?);/)[1];
    } catch (e) {
      mime = "image/png";
    }
  }

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], fileName, { type: mime });
}

/**
 * 将文件对象转换为 URL
 * @param {File} file - 文件对象
 * @returns {string} - 文件对象的 URL
 */
export function fileToUrl(file) {
  const windowURL = window.URL || window.webkitURL;
  return windowURL.createObjectURL(file);
}

export function releaseFileUrl(url) {
  try {
    const windowURL = window.URL || window.webkitURL;
    windowURL.revokeObjectURL(url);
  } catch (e) {
    console.log(e);
  }
}

export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    let reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (e) => {
      resolve(e.target.result);
    };
  });
}

export function blobUrlToBase64(blobUrl) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("GET", blobUrl, true);
    xhr.responseType = "blob";

    xhr.onload = () => {
      if (xhr.status === 200) {
        const reader = new FileReader();

        reader.onloadend = () => {
          const base64String = reader.result;
          resolve(base64String);
        };

        reader.onerror = (error) => {
          reject(error);
        };

        reader.readAsDataURL(xhr.response);
      } else {
        reject(new Error(`Failed to fetch Blob from URL: ${blobUrl}`));
      }
    };

    xhr.onerror = (error) => {
      reject(error);
    };

    xhr.send();
  });
}

export async function blobUrlToFile(blobUrl, type) {
  const response = await fetch(blobUrl);
  const blob = await response.blob();
  const fileName = getFileNameFromUrl(blobUrl);
  type = type || blob.type || "image/png";
  return new File([blob], fileName, { type: type });
}

export async function httpUrlToFile(url, fileName) {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const blob = await response.blob();
    const file = new File([blob], fileName, { type: blob.type });
    return file;
  } catch (error) {
    console.error("There has been a problem with your fetch operation:", error);
    return null;
  }
}

export function getFileNameFromUrl(url) {
  const path = url.split("/").pop();
  const fileName = path.split("#")[0].split("?")[0];
  return fileName;
}

/**
 * 根据文件大小返回适当的文件大小字符串
 * @param {number} size - 文件大小（以字节为单位）
 * @returns {string} - 格式化后的文件大小字符串
 */
export function getfilesize(size) {
  const num = 1024.0; // byte
  if (size < num) return size + " B";
  if (size < Math.pow(num, 2)) return (size / num).toFixed(2) + " KB"; // kb
  if (size < Math.pow(num, 3)) return (size / Math.pow(num, 2)).toFixed(2) + " MB"; // M
  if (size < Math.pow(num, 4)) return (size / Math.pow(num, 3)).toFixed(2) + " G"; // G
  return (size / Math.pow(num, 4)).toFixed(2) + " T"; // T
}

export function downloadFile(link, filename, callback) {
  if (window.client_env === "electron") {
    window.electronAPI.downloadFile(link, filename, callback);
  } else {
    window.open(link);
  }
}

export function downloadImage(link, picName) {
  if (window.client_env === "electron") {
    downloadFile(link, picName, () => {});
  } else {
    fetchFile(link)
      .then((res) => res.blob())
      .then((blob) => {
        let url = URL.createObjectURL(blob);
        let a = document.createElement("a");
        let event = new MouseEvent("click");
        a.download = picName || "default.png";
        a.href = url;
        a.dispatchEvent(event);
        setTimeout(() => URL.revokeObjectURL(url), 1000 * 10);
      });
  }
}

export function fetchFile(url) {
  let u = new URL(url);
  let params = u.searchParams;
  if (params) {
    params.append("x-fetch-with-no-cache", "1");
  }

  return fetch(String(u));
}

export function extensionFromFilename(filename) {
  if (!filename) return "";
  let parts = filename.split(".");
  let ext = parts.length > 1 ? parts[parts.length - 1] : "";
  return ext;
}

export function readBuffer(file, start = 0, end = 2) {
  return new Promise((resolve, reject) => {
    const length = Math.min(end - start, file.size - start);
    const reader = new FileReader();
    reader.onload = function (_) {
      resolve(reader.result);
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file.slice(start, start + length));
  });
}

/**
 * 检查给定对象是否为文件
 * @param {File} file - 要检查的文件对象
 * @returns {Promise<boolean>} - 返回一个 Promise，解析为 true 表示是文件，false 表示不是文件
 */
export function checkIsFile(file) {
  return new Promise((resolve, reject) => {
    try {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file.slice(0, 3));
      fileReader.addEventListener('load', function (e) {
        resolve(1)
      }, false);
      fileReader.addEventListener('error', function (e) {
        resolve(0)
      }, false);
    } catch (e) {
      resolve(0)
    }
  })
}

export function formatSpeed(bytesPerSecond) {
  let speed = bytesPerSecond;

  // 如果速度大于 1 MB/s
  if (speed > 1024 * 1024) {
    return (speed / (1024 * 1024)).toFixed(1) + ' MB/s';
  }

  // 如果速度大于 1 KB/s
  if (speed > 1024) {
    return (speed / 1024).toFixed(1) + ' KB/s';
  }

  return speed + ' B/s';
}

export function formatProgress(percent) {
  return Math.round(percent * 100)
}